import { useEffect, useMemo, useState } from "react";
import { Diff, Hunk, parseDiff, type HunkData } from "react-diff-view";

import { apiService } from "@/services/api.service";

import { buildPromptPatch } from "./promptPatch";

import "./promptTextDiff.css";

interface PromptTextDiffProps {
  applicationId: string;
  promptId: string;
  fromVersion: number;
  toVersion: number;
}

/**
 * Text diff of one prompt's content between two of its versions.
 *
 * Content is fetched lazily — only when a row is expanded — because a tutor version
 * can pin 39 prompts and eagerly loading every pair would be dozens of requests for
 * data the user probably will not look at.
 */
export const PromptTextDiff = ({
  applicationId,
  promptId,
  fromVersion,
  toVersion,
}: PromptTextDiffProps) => {
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "error"; message: string }
    | { kind: "ready"; hunks: HunkData[]; identical: boolean }
  >({ kind: "loading" });

  const counts = useMemo(() => {
    let insert = 0;
    let remove = 0;
    for (const hunk of state.kind === "ready" ? state.hunks : []) {
      for (const change of hunk.changes) {
        if (change.type === "insert") insert += 1;
        else if (change.type === "delete") remove += 1;
      }
    }
    return { insert, delete: remove };
  }, [state]);

  // The parent keys this component by prompt id, so a different prompt remounts it
  // and starts from the "loading" initial state. That keeps the effect free of a
  // synchronous setState, which would otherwise trigger a cascading render.
  useEffect(() => {
    let cancelled = false;

    Promise.all([
      apiService.getPromptVersion(applicationId, promptId, String(fromVersion)),
      apiService.getPromptVersion(applicationId, promptId, String(toVersion)),
    ])
      .then(([a, b]) => {
        if (cancelled) return;
        const oldText = a?.content ?? "";
        const newText = b?.content ?? "";
        if (oldText === newText) {
          setState({ kind: "ready", hunks: [], identical: true });
          return;
        }
        const files = parseDiff(
          buildPromptPatch(promptId, fromVersion, toVersion, oldText, newText),
        );
        setState({
          kind: "ready",
          hunks: files[0]?.hunks ?? [],
          identical: false,
        });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            kind: "error",
            message: err instanceof Error ? err.message : "Failed to load prompt versions",
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [applicationId, promptId, fromVersion, toVersion]);

  if (state.kind === "loading") {
    return (
      <p className="px-2 py-3 text-xs text-muted-foreground">Loading diff…</p>
    );
  }

  if (state.kind === "error") {
    return <p className="px-2 py-3 text-xs text-destructive">{state.message}</p>;
  }

  if (state.identical) {
    return (
      <p className="px-2 py-3 text-xs text-muted-foreground">
        The pin moved from v{fromVersion} to v{toVersion}, but the content of the two
        versions is identical.
      </p>
    );
  }

  return (
    <div className="space-y-2 py-2">
      <div className="flex items-center">
        <span className="ml-auto font-mono text-[11px]">
          <span className="text-primary">+{counts.insert}</span>{" "}
          <span className="text-destructive">−{counts.delete}</span>
        </span>
      </div>
      <div className="max-h-[55vh] overflow-auto rounded border">
        <Diff viewType="split" diffType="modify" hunks={state.hunks}>
          {(hunks: HunkData[]) =>
            hunks.map((hunk) => <Hunk key={hunk.content} hunk={hunk} />)
          }
        </Diff>
      </div>
    </div>
  );
};
